import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2 } from 'lucide-react';
import { Pick } from './ContactEditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUPS = [['case_note', 'case_note_template_ids', 'Case Note Templates'], ['email', 'email_template_ids', 'Email Templates'], ['sms', 'sms_template_ids', 'Text Templates']];

const TemplateChecklist = ({ title, items, selected, onToggle, testId }) => (
  <div data-testid={testId}>
    <p className="text-sm font-semibold mb-2">{title}</p>
    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
      {items.map((t) => (
        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => onToggle(t.id)} data-testid={`${testId}-${t.id}`} />{t.name}
        </label>
      ))}
      {!items.length && <p className="text-xs text-muted-foreground">No templates available.</p>}
    </div>
  </div>
);

export const TrackerSettingsDialog = ({ tab, open, onClose }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !tab) return;
    setData(null);
    axios.get(`${API}/referral-tracker/settings`, { params: { enrollment_type_id: tab.id } })
      .then((r) => { setData(r.data); setForm(r.data.settings); }).catch(() => toast.error('Could not load settings'));
  }, [open, tab]);
  if (!open) return null;

  const changeReferralType = async (v) => {
    setForm((f) => ({ ...f, referral_enrollment_type_id: v }));
    try {
      const r = await axios.get(`${API}/referral-tracker/settings`, { params: { enrollment_type_id: tab.id, referral_enrollment_type_id: v || undefined } });
      setData((d) => ({ ...d, templates: r.data.templates }));
    } catch {
      toast.error('Could not load templates');
    }
  };

  const toggle = (key) => (id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  const save = async () => {
    setSaving(true);
    try {
      const visible = (ch) => new Set((data.templates[ch] || []).map((t) => t.id));
      const pruned = { ...form };
      GROUPS.forEach(([ch, key]) => { const ids = visible(ch); pruned[key] = form[key].filter((id) => ids.has(id)); });
      await axios.put(`${API}/referral-tracker/settings`, pruned);
      toast.success('Referral Tracker settings saved');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="rt-settings-dialog">
        <DialogHeader>
          <DialogTitle>Referral Tracker Settings · {tab.name}</DialogTitle>
          <DialogDescription>Choose the enrollment added when a referral is uploaded, and the templates staff can use on this tab.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {!data || !form ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="space-y-6">
              <Pick label="Referral Enrollment (added on upload)" value={form.referral_enrollment_type_id}
                options={data.enrollment_types.map((t) => ({ value: t.id, label: t.name }))}
                onChange={changeReferralType} testId="rt-settings-referral-type" />
              <p className="text-xs text-muted-foreground -mt-4">If none is picked, the upload adds the {tab.name} enrollment. Template lists show templates from Message Templates assigned to this enrollment, plus ones available for everyone.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {GROUPS.map(([ch, key, title]) => (
                  <TemplateChecklist key={ch} title={title} items={data.templates[ch] || []} selected={form[key]} onToggle={toggle(key)} testId={`rt-settings-${ch}`} />
                ))}
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rt-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !form} data-testid="rt-settings-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BulkContact4Dialog = ({ open, tab, month, monthLabel, staff, onClose, onDone }) => {
  const [userId, setUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const save = async () => {
    if (!userId) return toast.error('Choose who Contact 4 is assigned to');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/referral-tracker/contact4/bulk`, { enrollment_type_id: tab.id, tracker_month: month, assigned_user_id: userId });
      toast.success(`Contact 4 added to ${data.added} referral(s). Skipped ${data.skipped_outreach_ended} where outreach ended, ${data.already_had_contact_4} already had it.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add Contact 4');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rt-bulk4-dialog">
        <DialogHeader>
          <DialogTitle>Add Contact 4 for {monthLabel}</DialogTitle>
          <DialogDescription>Adds Contact 4 to every referral in this tab and month where outreach is still open, and creates a task for the assigned user.</DialogDescription>
        </DialogHeader>
        <Pick label="Assign to" value={userId} options={staff.map((s) => ({ value: s.id, label: s.name }))} onChange={setUserId} testId="rt-bulk4-user" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rt-bulk4-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rt-bulk4-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Add Contact 4</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
